import {
  OCTAVE_DOWN_KEYS,
  OCTAVE_UP_KEYS,
  ROW_SHIFT_STEP,
  WHITE_DOWN_KEYS,
  WHITE_UP_KEYS,
} from '../config/keyboard-layouts';
import { clamp } from '../config/music';
import type { Settings } from '../state/settings';
import * as store from '../state/store';
import { panic, releaseNote, triggerNote } from '../audio/engine';
import { currentView } from './keyboard';
import { rowShiftBounds } from './keymap';

/**
 * All key interaction is delegated to a handful of container listeners.
 * Attaching mousedown + mouseenter to each key meant roughly 180 listeners that
 * had to be re-created on every re-render — including every frame of a
 * range-slider drag.
 *
 * Each input source owns one stable id ('mouse', 'touch-3', 'key-c'). Sliding
 * to another key just re-triggers the same id, and the engine moves that
 * holder from one pitch to the next; key highlighting follows from the engine
 * rather than being toggled here, so two sources on one key stay consistent.
 */

const MOUSE_ID = 'mouse';
const touchIds = new Set<number>();
const keysDown = new Set<string>();
let mouseDown = false;

/**
 * Playing is blocked while a sampler instrument is still loading, so the first
 * presses are not silently swallowed by an engine with no voices yet. Shifting
 * the mapping and the pedal stay allowed; only note triggering is gated.
 */
let inputEnabled = true;
export function setInputEnabled(enabled: boolean): void {
  inputEnabled = enabled;
}

function absFromTarget(target: EventTarget | null): number | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest('.key');
  if (!el) return null;
  return currentView().elToAbs.get(el) ?? null;
}

const absFromPoint = (x: number, y: number): number | null => absFromTarget(document.elementFromPoint(x, y));

export function installInput(pianoEl: HTMLElement): void {
  const s = (): Settings => store.getSettings() as Settings;

  // --- mouse: one note that follows the cursor while held, so pressing and
  // sliding sideways plays a glissando like a finger on a real keyboard.
  pianoEl.addEventListener('mousedown', (e) => {
    if (!inputEnabled) return;
    const abs = absFromTarget(e.target);
    if (abs === null) return;
    e.preventDefault();
    mouseDown = true;
    triggerNote(MOUSE_ID, abs, s());
  });

  pianoEl.addEventListener('mouseover', (e) => {
    if (!mouseDown) return;
    const abs = absFromTarget(e.target);
    if (abs !== null) triggerNote(MOUSE_ID, abs, s());
  });

  document.addEventListener('mouseup', () => {
    if (!mouseDown) return;
    mouseDown = false;
    releaseNote(MOUSE_ID, s());
  });

  // --- touch: tracked per finger, so several fingers each hold or glissando
  // their own note independently.
  pianoEl.addEventListener(
    'touchstart',
    (e) => {
      if (!inputEnabled) return;
      e.preventDefault();
      for (const touch of e.changedTouches) {
        const abs = absFromPoint(touch.clientX, touch.clientY);
        if (abs === null) continue;
        touchIds.add(touch.identifier);
        triggerNote(`touch-${touch.identifier}`, abs, s());
      }
    },
    { passive: false },
  );

  pianoEl.addEventListener(
    'touchmove',
    (e) => {
      e.preventDefault();
      for (const touch of e.changedTouches) {
        if (!touchIds.has(touch.identifier)) continue;
        const abs = absFromPoint(touch.clientX, touch.clientY);
        if (abs !== null) triggerNote(`touch-${touch.identifier}`, abs, s());
        else releaseNote(`touch-${touch.identifier}`, s());
      }
    },
    { passive: false },
  );

  const endTouch = (e: TouchEvent): void => {
    for (const touch of e.changedTouches) {
      if (!touchIds.delete(touch.identifier)) continue;
      releaseNote(`touch-${touch.identifier}`, s());
    }
  };
  pianoEl.addEventListener('touchend', endTouch);
  pianoEl.addEventListener('touchcancel', endTouch);

  // --- computer keyboard
  document.addEventListener('keydown', (e) => {
    const active = document.activeElement;
    if (active instanceof HTMLElement && ['SELECT', 'INPUT', 'TEXTAREA'].includes(active.tagName)) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    const settings = s();
    // e.key already carries the shifted character, so _ / + arrive distinct
    // from − / =. Lowercase only the plain keys, which never overlap these.
    const raw = e.key;
    const ch = raw.toLowerCase();

    // Move everything the keyboard plays, before note handling: − / = / arrows
    // by a whole octave, their shifted forms _ / + by a single white key. None
    // is ever a note key.
    const octaveDir = OCTAVE_DOWN_KEYS.includes(ch) ? -1 : OCTAVE_UP_KEYS.includes(ch) ? 1 : 0;
    const whiteDir = WHITE_DOWN_KEYS.includes(raw) ? -1 : WHITE_UP_KEYS.includes(raw) ? 1 : 0;
    if (octaveDir !== 0 || whiteDir !== 0) {
      e.preventDefault();
      shift(octaveDir, whiteDir, settings);
      return;
    }

    if (!inputEnabled) return;
    if (keysDown.has(ch)) return;
    const abs = currentView().bindings.byKey.get(ch);
    if (abs === undefined) return;
    e.preventDefault();
    keysDown.add(ch);
    triggerNote(`key-${ch}`, abs, settings);
  });

  document.addEventListener('keyup', (e) => {
    const ch = e.key.toLowerCase();
    if (!keysDown.delete(ch)) return;
    releaseNote(`key-${ch}`, s());
  });

  // Losing focus (alt-tab) never delivers keyup, which used to leave notes
  // droning forever.
  window.addEventListener('blur', () => {
    keysDown.clear();
    touchIds.clear();
    mouseDown = false;
    panic(s());
  });
}

/**
 * Slide the contiguous mapping window by `deltaWhites` white keys (a whole
 * octave = 7 for − / =, a single white key for _ / +), clamped so it stays
 * inside the range. Works the same on one or two display rows because the
 * window is one block, not one-per-row.
 */
function shift(octaveDir: number, whiteDir: number, s: Settings): void {
  const deltaWhites = octaveDir !== 0 ? octaveDir * ROW_SHIFT_STEP : whiteDir;
  const { min, max } = rowShiftBounds(s);
  const next = clamp(s.rowShift + deltaWhites, min, max);
  if (next === s.rowShift) return;
  panic(s);
  store.update({ rowShift: next });
}

/** Shift the mapping from a UI button — same effect as the − = _ + keys. */
export function shiftMapping(octaveDir: number, whiteDir: number): void {
  shift(octaveDir, whiteDir, store.getSettings() as Settings);
}
